import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { stringify } from 'querystring';
import { FormattedMessage } from 'react-intl';
import { useApolloClient } from '@apollo/client';

import CanPerform from 'components/shared/CanPerform';
import { EXCHANGE } from 'constants/afterSalesShortcuts';
import { bookingStatus } from 'types/booking';
import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import filterLegsByProductItemRefs from 'utils/filterLegsByProductItemRefs';
import { removeEmptyValues } from 'utils/functions';
import formatReason from 'utils/formatReason';
import moment from 'moment';
import ChildrenWillTravelAloneWarning from '../../AfterSalesWarning/ChildrenWillTravelAloneWarning/ChildrenWillTravelAloneWarning';
import SubsetOfPassengersInPalsBookingWarning from '../../AfterSalesWarning/SubsetOfPassengersInPalsBookingWarning/SubsetOfPassengersInPalsBookingWarning';
import NotYetInStarlightWarning from '../../AfterSalesWarning/NotYetInStarlightWarning/NotYetInStarlightWarning';
import ARE_TICKETS_IN_SALESFORCE from 'store/queries/ARE_TICKETS_IN_SALESFORCE';

import {
  runAfterSaleValidator,
  allSegmentsHaveSameSelectedPassengers,
  allSegmentsCanBeRebooked,
  allSegmentsCannotBeRebooked,
  userCanOverrideAfterSalesRules,
  selectedProductsCheckedIn,
  productHasNotBeenPrrRefunded,
  allSegmentsInOneJourney,
  passengersWithMixedJourneysInSegments,
  canceledPassengersInSegments,
  allSegmentsOfType,
  selectedProductsHaveDifferentProductFamily,
  hasActiveAOP,
  hasActiveInsurance,
  areAllSelectedProductsLoyal,
  containsSNCBItemRefs,
  getFaresForPassengersInMixedJourney,
  getProductFamiliesPerPassengersInMixedPF,
  getSegmentsForItemRefs,
  hasDBTicketsSelected
} from '../AfterSalesCtas.utils';
import { CtaButton } from '../AfterSalesCtas.styles';
import WarningCta from '../WarningCta/WarningCta';

import { findEarliest } from './ExchangeCta.utils';
import ExchangeCtaOverride from './ExchangeCtaOverride';
import ExchangeCtaPackageBookingWarning from './ExchangeCtaPackageBookingWarning';
import ExchangeCtaSNCBBookingWarning from './ExchangeCtaSNCBBookingWarning';
import { BP, PREMIER, PREMIUM } from 'constants/classOfService';

const ExchangeCta = ({
  history: {
    push,
    location: { search }
  },
  label,
  disabled,
  booking,
  booking: { outbound, inbound, allBookingItems, status },
  selectedItemRefs,
  user,
  isPackageBooking = false,
  subscription = null,
  willResultInChildAlone = false,
  exchangingSubsetOfPaxsInPALBooking = false
}) => {
  const client = useApolloClient();
  const isProvisionalBooking = status === bookingStatus.PROVISIONAL;
  const isConfirmedBooking = status === bookingStatus.CONFIRMED;
  const isStationUser = user?.isStation;
  const isSNCBLegSelected =
    selectedItemRefs.length > 0 &&
    (containsSNCBItemRefs(selectedItemRefs, booking.outbound, false) || containsSNCBItemRefs(selectedItemRefs, booking.inbound, false));
  const showSNCBWarning = isStationUser && isConfirmedBooking && isSNCBLegSelected;

  const segments = booking && Array.from(getSegmentsForItemRefs(booking, selectedItemRefs));
  const hasDBTickets = hasDBTicketsSelected({ segments });
  const [ticketsInSalesforceLoading, setTicketsInSalesforceLoading] = useState(false);
  const [blockAftersalesAsNotYetInStarlight, setBlockAftersalesAsNotYetInStarlight] = useState(false);
  const [blockAftersalesAsChildrenWillTravelAlone, setBlockAftersalesAsChildrenWillTravelAlone] = useState(false);
  const [warningSubsetOfPaxsInPals, setWarningSubsetOfPaxsInPals] = useState(false);
  const membershipNumber = new URLSearchParams(search).get('membershipNumber');
  const isExchangePossible =
    selectedItemRefs.length > 0 &&
    runAfterSaleValidator(booking, selectedItemRefs)([allSegmentsInOneJourney]) &&
    runAfterSaleValidator(booking, selectedItemRefs, user, 'some')([allSegmentsCanBeRebooked, userCanOverrideAfterSalesRules]) &&
    !runAfterSaleValidator(booking, selectedItemRefs)([selectedProductsCheckedIn('some')]) &&
    runAfterSaleValidator(booking, selectedItemRefs)([productHasNotBeenPrrRefunded]);

  const isExchangePossibleBasedOnServiceAndPassengers = hasDBTickets
    ? true
    : runAfterSaleValidator(booking, selectedItemRefs)([allSegmentsHaveSameSelectedPassengers]);
  const hasPassengersWithMixedJourneys = runAfterSaleValidator(
    booking,
    selectedItemRefs,
    null,
    'some'
  )([canceledPassengersInSegments, passengersWithMixedJourneysInSegments]);

  const isOverridingRules = runAfterSaleValidator(
    booking,
    selectedItemRefs,
    user
  )([allSegmentsCannotBeRebooked, userCanOverrideAfterSalesRules]);

  const hasMoreThanOneProductFamily = runAfterSaleValidator(booking, selectedItemRefs)([selectedProductsHaveDifferentProductFamily]);

  const faresForPassengersInMixedJourney = getFaresForPassengersInMixedJourney({
    booking,
    selectedItemRefs,
    hasPassengersWithMixedJourneys
  });
  const productFamiliesPerPassengersInMixedPF = getProductFamiliesPerPassengersInMixedPF({
    booking,
    selectedItemRefs,
    hasMoreThanOneProductFamily
  });
  const hasInsurance = runAfterSaleValidator(booking, selectedItemRefs)([hasActiveInsurance]);
  const isExchangeDisabled = disabled || !isExchangePossible || !isExchangePossibleBasedOnServiceAndPassengers;

  const selectedTicketNumbers = selectedItemRefs
    .flatMap((ref) => allBookingItems.filter((item) => ref === item.itemRef))
    .map((item) => item.ticketNumber);

  const handleExchanges = ({ reason, reasonLabel, reasonInfo, subsetOfPaxsPals, shouldCheckPals }) => {
    if (willResultInChildAlone) {
      return setBlockAftersalesAsChildrenWillTravelAlone(true);
    }

    if (shouldCheckPals && exchangingSubsetOfPaxsInPALBooking && !subsetOfPaxsPals) {
      return setWarningSubsetOfPaxsInPals(true);
    }

    let outboundDate = findEarliest(filterLegsByProductItemRefs(outbound, selectedItemRefs));
    let inboundDate = findEarliest(filterLegsByProductItemRefs(inbound, selectedItemRefs));

    if (
      isOverridingRules ||
      runAfterSaleValidator(booking, selectedItemRefs, null, 'some')([allSegmentsOfType([BP, PREMIUM, PREMIER]), hasActiveAOP])
    ) {
      outboundDate = outboundDate && moment(outboundDate).isBefore(moment()) ? moment().format('YYYY-MM-DD') : outboundDate;
      inboundDate = inboundDate && moment(inboundDate).isBefore(moment()) ? moment().format('YYYY-MM-DD') : inboundDate;
    }
    const searchObject = removeEmptyValues({
      bookingItems: selectedItemRefs.join(','),
      outboundDate,
      inboundDate,
      membershipNumber,
      subscriptionStartDate: subscription?.startDate ? moment(subscription.startDate).format('YYYY-MM-DD') : null,
      subscriptionEndDate: subscription?.endDate ? moment(subscription.endDate).format('YYYY-MM-DD') : null
    });

    push({
      pathname: `/exchange/${booking.reference}`,
      search: stringify(searchObject),
      state: reason ? { overrideReason: formatReason(reason, reasonInfo, reasonLabel) } : {}
    });
  };

  const handleCheckTicketsInSalesforce = async ({ reason, reasonLabel, reasonInfo, subsetOfPaxsPals, shouldCheckPals }) => {
    if (areAllSelectedProductsLoyal({ selectedItemRefs, booking })) {
      setTicketsInSalesforceLoading(true);
      try {
        await client
          .query({
            query: ARE_TICKETS_IN_SALESFORCE,
            variables: {
              bookingReference: booking.reference,
              ticketNumbers: selectedTicketNumbers
            }
          })
          .then((response) => {
            !response.data.areTicketsInSalesforce
              ? setBlockAftersalesAsNotYetInStarlight(true)
              : handleExchanges({ reason, reasonLabel, reasonInfo, subsetOfPaxsPals, shouldCheckPals });
          });
      } catch {
        throw new Error('Cannot fetch tickets in salesforce');
      } finally {
        setTicketsInSalesforceLoading(false);
      }
    } else {
      handleExchanges({ reason, reasonLabel, reasonInfo, subsetOfPaxsPals, shouldCheckPals });
    }
  };

  const handleClick = (reason, reasonLabel, reasonInfo, subsetOfPaxsPals, shouldCheckPals = true) => {
    if (disabled) {
      return;
    }

    handleCheckTicketsInSalesforce({ reason, reasonLabel, reasonInfo, subsetOfPaxsPals, shouldCheckPals });
  };

  const handleClickWithoutPals = (reason, reasonLabel, reasonInfo) => {
    handleClick(reason, reasonLabel, reasonInfo, false, false);
  };

  const handleClickWithoutReason = () => handleClick();

  if (blockAftersalesAsNotYetInStarlight) {
    return <NotYetInStarlightWarning isOpen={true} closeModal={() => setBlockAftersalesAsNotYetInStarlight(false)} />;
  }

  if (blockAftersalesAsChildrenWillTravelAlone) {
    return <ChildrenWillTravelAloneWarning isOpen={true} closeModal={() => setBlockAftersalesAsChildrenWillTravelAlone(false)} />;
  }

  return (
    <CanPerform afterSalesShortcut={EXCHANGE}>
      {(isEligible) =>
        isEligible &&
        (isOverridingRules && !isProvisionalBooking ? (
          <ExchangeCtaOverride
            isExchangeDisabled={isExchangeDisabled || ticketsInSalesforceLoading}
            label={label}
            onClick={handleClick}
            selectedItemRefs={selectedItemRefs}
            booking={booking}
            isPackageBooking={isPackageBooking}
            showSNCBWarning={showSNCBWarning}
            exchangingSubsetOfPaxsInPALBooking={exchangingSubsetOfPaxsInPALBooking}
            hasInsurance={hasInsurance}
          />
        ) : (hasPassengersWithMixedJourneys || hasMoreThanOneProductFamily || (hasInsurance && !isPackageBooking)) &&
          !isExchangeDisabled ? (
          <WarningCta
            label={label}
            title={<FormattedMessage id={hasInsurance ? 'exchanges.insuranceWarning.title' : 'afterSales.warning'} />}
            subtitle={
              !(hasInsurance && !isPackageBooking) ? (
                <FormattedMessage
                  id={hasMoreThanOneProductFamily ? 'exchanges.productFamilyWarning' : 'afterSales.warningSelectionMessage'}
                />
              ) : null
            }
            isPackageBooking={isPackageBooking}
            showConfirmButton={hasInsurance}
            onConfirmClick={handleClickWithoutReason}
            hasInsurance={hasInsurance}
            hasMoreThanOneProductFamily={hasMoreThanOneProductFamily}
            hasPassengersWithMixedJourneys={hasPassengersWithMixedJourneys}
            faresForPassengersInMixedJourney={faresForPassengersInMixedJourney}
            productFamiliesPerPassengersInMixedPF={productFamiliesPerPassengersInMixedPF}
          />
        ) : warningSubsetOfPaxsInPals ? (
          <SubsetOfPassengersInPalsBookingWarning
            isOpen={true}
            closeModal={() => setWarningSubsetOfPaxsInPals(false)}
            handleClick={() => handleClickWithoutPals()}
          />
        ) : isPackageBooking ? (
          <ExchangeCtaPackageBookingWarning
            type="button"
            data-cy="exchange-cta-show-package-warning"
            isExchangeDisabled={isExchangeDisabled || ticketsInSalesforceLoading}
            label={label}
            onClick={() => handleClickWithoutPals()}
            hasInsurance={hasInsurance}
          />
        ) : showSNCBWarning ? (
          <ExchangeCtaSNCBBookingWarning
            type="button"
            data-cy="exchanges.sncbStationBookingSelected"
            isExchangeDisabled={isExchangeDisabled || ticketsInSalesforceLoading}
            label={label}
            onClick={() => handleClickWithoutPals()}
            hasInsurance={hasInsurance}
          />
        ) : (
          <CtaButton
            type="button"
            data-cy="exchange-cta"
            disabled={isExchangeDisabled || ticketsInSalesforceLoading}
            loading={ticketsInSalesforceLoading}
            label={label}
            onClick={handleClickWithoutReason}
          >
            {label}
          </CtaButton>
        ))
      }
    </CanPerform>
  );
};

ExchangeCta.propTypes = {
  user: userShape,
  booking: bookingShape,
  history: historyShape,
  label: PropTypes.element,
  disabled: PropTypes.bool,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  isPackageBooking: PropTypes.bool,
  subscription: PropTypes.shape({
    bookingReference: PropTypes.string,
    travelPassReference: PropTypes.string,
    code: PropTypes.string,
    status: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  willResultInChildAlone: PropTypes.bool,
  exchangingSubsetOfPaxsInPALBooking: PropTypes.bool
};

export default ExchangeCta;
